/* eslint-disable no-console */

/**
 * In-browser translators (Google Translate, Safari/Edge translation) rewrite text nodes in the
 * live DOM — wrapping them in <font> tags and reparenting them — while React keeps references to
 * the original nodes. On the next commit React calls removeChild/insertBefore with nodes that are
 * no longer where it left them, throwing "NotFoundError: Failed to execute 'removeChild' on
 * 'Node'" and unmounting the whole tree via the nearest error boundary.
 *
 * There is no upstream fix (https://github.com/facebook/react/issues/11538); the accepted
 * workaround is to make these two methods degrade gracefully. Each hit is still logged (once per
 * method per page load, forwarded to Datadog via forwardConsoleLogs) so genuine misuse of the DOM
 * API doesn't get masked silently.
 */

let installed = false;
const reported = new Set<string>();

const report = (method: string) => {
  if (reported.has(method)) {
    return;
  }
  reported.add(method);

  // Google Translate marks the document root with a "translated-ltr" / "translated-rtl" class.
  const translated = /translated/.test(document.documentElement.className);
  console.warn(
    `domTranslationGuard: suppressed Node.prototype.${method} on a reparented node (page translated: ${translated})`,
  );
};

export function installDomTranslationGuard(): void {
  if (installed || typeof Node !== 'function' || !Node.prototype) {
    return;
  }
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      report('removeChild');
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      report('insertBefore');
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
